import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { getAuthItem } from "../../../public/utils/authStorage";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { getMyMembershipsOverview } from "../api/studentMembershipApi";
import { EMPTY_MY_MEMBERSHIPS_OVERVIEW } from "../utils/myMembershipsConstants";
import MyMembershipsDashboardSection from "../components/my-memberships/MyMembershipsDashboardSection";
import MyMembershipsSummarySection from "../components/my-memberships/MyMembershipsSummarySection";
import MyMembershipsTimelineSection from "../components/my-memberships/MyMembershipsTimelineSection";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/MyMembershipsPage.css";

function readAuthState() {
  const token = getAuthItem("jwt_token");
  return {
    hasToken: Boolean(token),
    roleSlug: token ? getJwtRoleSlug(token) || "" : "",
  };
}

export default function MyMembershipsPage() {
  const [auth, setAuth] = useState(readAuthState);
  const [overview, setOverview] = useState(EMPTY_MY_MEMBERSHIPS_OVERVIEW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isStudent = auth.roleSlug === "students";
  const showStudentLoginGate = auth.hasToken && !isStudent;

  useEffect(() => {
    setAuth(readAuthState());
  }, []);

  useEffect(() => {
    if (showStudentLoginGate || !auth.hasToken) {
      setOverview(EMPTY_MY_MEMBERSHIPS_OVERVIEW);
      setLoading(false);
      setError("");
      return;
    }

    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getMyMembershipsOverview();
        if (!alive) return;
        setOverview(res?.data || EMPTY_MY_MEMBERSHIPS_OVERVIEW);
      } catch (err) {
        if (!alive) return;
        if (err?.code === "STUDENT_ROLE_REQUIRED") {
          setOverview(EMPTY_MY_MEMBERSHIPS_OVERVIEW);
          setError("");
          return;
        }
        setError(err?.message || "Failed to load membership overview");
        setOverview(EMPTY_MY_MEMBERSHIPS_OVERVIEW);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [auth.hasToken, isStudent, showStudentLoginGate]);

  const studentName = useMemo(
    () => overview?.student?.full_name || "",
    [overview?.student?.full_name]
  );

  if (showStudentLoginGate) {
    return (
      <div className="app-screen-guard app-screen-guard--blocked">
        <h2 className="app-screen-guard__title">Student login required</h2>
        <p className="app-screen-guard__text">
          You must login as student to experience this page.
        </p>
        <div className="app-screen-guard__actions">
          <Link to="/student-login" className="app-screen-guard__link">
            Go to student login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mm-shell">
      <header className="mm-hero">
        <p className="mm-hero__eyebrow">Student portal</p>
        <h1 className="mm-hero__title">My memberships</h1>
        <p className="mm-hero__sub">
          {auth.hasToken && isStudent && studentName
            ? `Analyse membership activity for ${studentName}. This screen is read-only.`
            : "Analyse your membership applications and renewals. Sign in as a student to load your records."}
        </p>
      </header>

      <GlobalFeedback type="error" text={error} />

      <MyMembershipsDashboardSection
        dashboard={overview.dashboard}
        loading={loading && auth.hasToken}
      />

      <MyMembershipsSummarySection
        summary={overview.summary}
        timeline={overview.timeline}
        loading={loading && auth.hasToken}
      />

      <MyMembershipsTimelineSection
        timeline={overview.timeline}
        loading={loading && auth.hasToken}
      />
    </div>
  );
}
